import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Globe, AtSign, Loader2, ShieldCheck, ArrowUpRight, Heart, Info, Copy, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fetchConfig, fetchTweetPreview, createHypeListing } from "@/lib/api";

const NONE = "__none__";

export default function List() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
    const specialties = config.data?.specialties || [];

    const [url, setUrl] = useState(() => params.get("url") || "");
    const [handle, setHandle] = useState(() => (params.get("handle") || "").replace(/^@/, ""));
    const [description, setDescription] = useState("");
    const [specialty, setSpecialty] = useState(NONE);
    const [email, setEmail] = useState("");
    const [tweetUrl, setTweetUrl] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState("");
    const [busyPreview, setBusyPreview] = useState(false);
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const canOpenX = !!preview?.intent_url && !busyPreview;
    const canSubmit = url.trim().length > 0 && handle.trim().length > 0 && tweetUrl.trim().length > 0 && !busy;

    useEffect(() => {
        const raw = url.trim();
        if (!raw) {
            setPreview(null);
            setPreviewError("");
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setBusyPreview(true);
            try {
                const data = await fetchTweetPreview(raw);
                if (cancelled) return;
                setPreview(data);
                setPreviewError("");
            } catch (e) {
                if (cancelled) return;
                setPreview(null);
                setPreviewError(e?.response?.data?.detail || "Enter a valid app URL to build the tweet.");
            } finally {
                if (!cancelled) setBusyPreview(false);
            }
        }, 400);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [url]);

    const copyTweet = async () => {
        if (!preview?.text) return;
        try {
            await navigator.clipboard.writeText(preview.text);
            setCopied(true);
            toast.success("Tweet text copied. Paste it on X, then paste the post URL below.");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Could not copy. Select the text and copy it.");
        }
    };

    const openTweet = () => {
        if (!preview?.intent_url) return;
        const win = window.open(preview.intent_url, "_blank", "noopener,noreferrer");
        if (!win) {
            toast.error("Pop-up blocked. Copy the tweet, or tap Open compose on x.com.");
        }
    };

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            await createHypeListing({
                url: url.trim(),
                x_handle: handle.trim(),
                tweet_url: tweetUrl.trim(),
                description: description.trim() || undefined,
                specialty: specialty === NONE ? "" : specialty,
                email: email.trim() || undefined,
            });
            toast.success("You're on the board.");
            navigate("/");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not list that tweet. Check the URL.";
            toast.error(msg);
            setBusy(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <div className="mb-8">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">List your fitness app</h1>
                <p className="text-muted-foreground mt-2">
                    Tweet the template. Paste the post. Rank is likes. No account and no payment.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <Card className="md:col-span-7 bg-card border-border p-5 sm:p-6">
                    <div className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="list-url">App link / website</Label>
                            <div className="relative">
                                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="list-url"
                                    data-testid="list-url-input"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="yourfitnessapp.com (or App Store URL)"
                                    className="pl-9"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="list-handle">Your X handle</Label>
                            <div className="relative">
                                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="list-handle"
                                    data-testid="list-handle-input"
                                    value={handle}
                                    onChange={(e) => setHandle(e.target.value.replace(/^@/, ""))}
                                    placeholder="yourapphandle"
                                    className="pl-9"
                                />
                            </div>
                            <p className="text-xs text-muted-foreground">The listing tweet must come from this account.</p>
                        </div>

                        <div className="space-y-2">
                            <Label>Specialty <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Select value={specialty} onValueChange={setSpecialty}>
                                <SelectTrigger data-testid="list-specialty-select">
                                    <SelectValue placeholder="e.g. Workout Tracker, AI Workout" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NONE}>No specialty</SelectItem>
                                    {specialties.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="list-desc">Short pitch <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Textarea
                                id="list-desc"
                                data-testid="list-description-input"
                                maxLength={200}
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="AI workout tracker with Apple Watch sync."
                            />
                            <p className="text-xs text-muted-foreground text-right">{description.length}/200</p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="list-email">Email <span className="text-muted-foreground font-normal">(optional)</span></Label>
                            <Input
                                id="list-email"
                                data-testid="list-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                            />
                            <p className="text-xs text-muted-foreground">Email me if someone passes my likes. No account created.</p>
                        </div>

                        <div className="space-y-2">
                            <Label>1. Prepared tweet</Label>
                            <p className="text-xs text-muted-foreground">
                                Copy this text, or open X with it filled in. Publish from the handle you entered, then paste the post URL.
                            </p>
                            <div
                                data-testid="list-tweet-preview"
                                className="text-sm rounded-lg border border-border bg-secondary/40 p-3 whitespace-pre-wrap min-h-[7.5rem]"
                            >
                                {busyPreview && !preview ? (
                                    <span className="text-muted-foreground inline-flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Building tweet…
                                    </span>
                                ) : previewError ? (
                                    <span className="text-heat">{previewError}</span>
                                ) : preview?.text ? (
                                    preview.text
                                ) : (
                                    <span className="text-muted-foreground">Enter your app URL to generate the tweet.</span>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    type="button"
                                    data-testid="list-copy-tweet-button"
                                    disabled={!preview?.text}
                                    onClick={copyTweet}
                                    variant="outline"
                                    className="font-semibold"
                                >
                                    {copied ? <Check className="h-4 w-4 mr-1.5 text-brand" /> : <Copy className="h-4 w-4 mr-1.5" />}
                                    {copied ? "Copied" : "Copy tweet"}
                                </Button>
                                <Button
                                    type="button"
                                    data-testid="list-tweet-button"
                                    disabled={!canOpenX}
                                    onClick={openTweet}
                                    className="bg-brand text-primary-foreground hover:bg-brand-dim font-semibold"
                                >
                                    Open on X <ArrowUpRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                            {preview?.intent_url ? (
                                <a
                                    data-testid="list-intent-link"
                                    href={preview.intent_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-brand hover:underline inline-flex items-center gap-1"
                                >
                                    Open compose on x.com <ArrowUpRight className="h-3 w-3" />
                                </a>
                            ) : null}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="list-tweet">2. Paste the tweet URL</Label>
                            <Input
                                id="list-tweet"
                                data-testid="list-tweet-url-input"
                                value={tweetUrl}
                                onChange={(e) => setTweetUrl(e.target.value)}
                                placeholder="https://x.com/you/status/123"
                            />
                        </div>

                        <Button
                            data-testid="list-submit-button"
                            disabled={!canSubmit}
                            onClick={submit}
                            className="w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold text-base h-11"
                        >
                            {busy ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Listing…</>
                            ) : (
                                <>List me on the board</>
                            )}
                        </Button>
                    </div>
                </Card>

                <div className="md:col-span-5 space-y-4">
                    <Card className="bg-card border-border p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">How rank works</h3>
                        <div className="rounded-xl border border-brand/30 bg-brand/5 p-4">
                            <div className="font-heading text-2xl font-bold inline-flex items-center gap-2">
                                <Heart className="h-6 w-6 text-brand" /> Likes
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                Crowd #1 is the listing tweet with the most likes. Earlier list wins a tie.
                            </p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 flex gap-1.5">
                            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            The tweet must name your app URL and outbid.fit. Likes can take a few minutes to refresh.
                        </p>
                    </Card>
                    <Card className="bg-card border-border p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">Quick rules</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Fitness apps, trackers, digital products only.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> Real App Store, Play Store, or website — no shorteners.</li>
                            <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-brand shrink-0 mt-0.5" /> One listing per app. One tweet per listing.</li>
                        </ul>
                        <Button variant="link" onClick={() => navigate("/rules")} className="px-0 mt-2 text-brand" data-testid="list-see-rules-link">
                            See full rules →
                        </Button>
                    </Card>
                </div>
            </div>
        </div>
    );
}
